import React, { useEffect, useRef } from 'react';
import { Dialog, DialogContent, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';

import DropWaterDetailSidebar from './dropWaterDetailSidebar';
import { RootState } from '../../store';

type PointsDetailItemLike = {
  date: string;
  description: string;
  pointsType: string;
  addAmount: number;
  reduceAmount: number;
  name: string
};

export default function DropWaterDetailDialog(props: {
  open: boolean;
  onClose: () => void;
  pointsDetailLoading: boolean;
  pointsDetailList: PointsDetailItemLike[];
  pointsDetailLoadingMore: boolean;
  onLoadMore: () => void;
  hasMore: boolean;
}) {
  const {
    open,
    onClose,
    pointsDetailLoading,
    pointsDetailList,
    pointsDetailLoadingMore,
    onLoadMore,
    hasMore,
  } = props;

  const isMobile = useMediaQuery('(max-width: 768px)');
  const memberInfo = useSelector((state: RootState) => state.member.memberInfo);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const formatPointsDetailDate = (isoDate: string): string => {
    try {
      const d = new Date(isoDate);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const h = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${day} ${h}:${min}`;
    } catch {
      return isoDate;
    }
  };

  const getPointsDetailDescriptionLabel = (description: string): string => {
    if (!description) return description;
    if (description.includes('消费支付: 生图')) return '图片生成';
    if (description.includes('消费支付: tokens')) return '文本生成';
    return description;
  };

  const POINTS_TYPE_LABEL_MAP: Record<string, string> = {
    flowing_water: '免费水滴',
    member_drops: '会员水滴',
    other_drops: '其他水滴',
    supply_drop: '补给水滴',
  };

  useEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (!el) return;
    const shouldAutoLoad = el.scrollHeight <= el.clientHeight + 1;
    if (shouldAutoLoad && hasMore && !pointsDetailLoading && !pointsDetailLoadingMore) {
      onLoadMore();
    }
  }, [open, hasMore, pointsDetailLoading, pointsDetailLoadingMore, pointsDetailList.length, onLoadMore]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: 'none',
          borderRadius: isMobile ? 0 : '20px',
        },
        '& .MuiDialogContent-root': {
          padding: '0',
        },
      }}
    >
      <DialogContent>
        <div className="dropWaterDetail">
          <div className="title">
            <span>
              水滴详情
             {/* <span style={{ color: 'var(--c-text-secondary)', fontSize: '14px', fontWeight: 500 }}>
                &nbsp;公测特惠：当前每次成功生图统一消耗10水滴（不限模型/尺寸），后续如有调整将及时告知。
              </span>*/}
            </span>
            <span
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </span>
          </div>

          <div className="dropWaterDetailLayout">
            <div className="dropWaterDetailMobileSidebarWrap"><DropWaterDetailSidebar memberInfo={memberInfo} /></div>
            <div className="dropWaterDetailMain pc-only">
              <div className="dropWaterDetailTableHeader">
                <div className="dropWaterDetailCol400">水滴明细</div>
                <div className="dropWaterDetailCol130">水滴类型</div>
                <div className="dropWaterDetailCol200">日期</div>
                <div className="dropWaterDetailCol70">水滴变动</div>
              </div>
              <div
                ref={bodyRef}
                className="dropWaterDetailTableBody"
                onScroll={(event) => {
                  if (!hasMore || pointsDetailLoading || pointsDetailLoadingMore) return;
                  const target = event.currentTarget;
                  const isNearBottom =
                    target.scrollHeight - target.scrollTop - target.clientHeight < 80;
                  if (isNearBottom) {
                    onLoadMore();
                  }
                }}
              >
                {pointsDetailLoading ? (
                  <div className="dropWaterDetailTableRow" style={{ justifyContent: 'center', color: 'var(--c-text-secondary)' }}>
                    加载中...
                  </div>
                ) : pointsDetailList.length === 0 ? (
                  <div className="dropWaterDetailTableRow" style={{ justifyContent: 'center', color: 'var(--c-text-secondary)' }}>
                    暂无消耗明细
                  </div>
                ) : (
                  <>
                    {pointsDetailList.map((item, index) => (
                      <div key={`${item.date}-${index}`} className="dropWaterDetailTableRow">
                        <div className="dropWaterDetailCol400Text">
                          {getPointsDetailDescriptionLabel(item.name || item.description)}
                        </div>
                        <div className="dropWaterDetailCol130">
                          {POINTS_TYPE_LABEL_MAP[item.pointsType] ?? item.pointsType}
                        </div>
                        <div className="dropWaterDetailCol200">{formatPointsDetailDate(item.date)}</div>
                        <div
                          className={
                            item.reduceAmount > 0 ? 'dropWaterDetailCol70Red' : 'dropWaterDetailCol70Green'
                          }
                        >
                          {item.addAmount > 0
                            ? `+${item.addAmount}`
                            : item.reduceAmount > 0
                              ? `-${item.reduceAmount}`
                              : '0'}
                        </div>
                      </div>
                    ))}
                    {pointsDetailLoadingMore && (
                      <div className="dropWaterDetailTableRow" style={{ justifyContent: 'center', color: 'var(--c-text-secondary)' }}>
                        加载中...
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{ color: 'var(--c-text-secondary)', fontSize: '14px', padding: '8px' }}>
                仅展示近7天的明细，数据更新可能有延时
              </div>
            </div>
            {isMobile && (
              <div className="dropWaterDetailMobileWrap">
                <div className="dropWaterDetailMobileList">
                  {pointsDetailLoading ? (
                    <div style={{ textAlign: 'center', color: 'var(--c-text-secondary)', padding: '40px 0' }}>加载中...</div>
                  ) : pointsDetailList.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'var(--c-text-secondary)', padding: '40px 0' }}>暂无消耗明细</div>
                  ) : (
                    <>
                      {pointsDetailList.map((item, index) => (
                        <div key={`${item.date}-${index}`} className="dropWaterDetailMobileItem">
                          <div className="dropWaterDetailMobileLeft">
                            <div className="dropWaterDetailMobileName">
                              {getPointsDetailDescriptionLabel(item.name || item.description)}
                            </div>
                            <div className="dropWaterDetailMobileTime">{formatPointsDetailDate(item.date)}</div>
                          </div>
                          <div className="dropWaterDetailMobileRight">
                            <div className={item.reduceAmount > 0 ? 'dropWaterDetailMobileAmountRed' : 'dropWaterDetailMobileAmountGreen'}>
                              {item.addAmount > 0 ? `+${item.addAmount}` : item.reduceAmount > 0 ? `-${item.reduceAmount}` : '0'}
                            </div>
                            <div className="dropWaterDetailMobileType">
                              {POINTS_TYPE_LABEL_MAP[item.pointsType] ?? item.pointsType}
                            </div>
                          </div>
                        </div>
                      ))}
                      {pointsDetailLoadingMore && (
                        <div style={{ textAlign: 'center', color: 'var(--c-text-secondary)', padding: '20px 0' }}>加载中...</div>
                      )}
                    </>
                  )}
                </div>
                <div className="dropWaterDetailMobileFooter">仅展示近7天的明细，数据更新可能有延迟</div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

